<template>
  <section v-if="totalCount > 0" class="grid grid-cols-12">
    <h3
      class="text-black col-span-9 col-start-1 row-end-2 row-start-1 mb-4 text-lg font-semibold leading-tight sm:col-span-6 md:col-span-7 md:mb-8 md:text-2xl md:font-bold lg:col-span-7 xl:col-span-6"
    >
      {{ tc_('label', totalCount) }}
    </h3>
    <ul v-if="amendedCategories.length > 0" class="grid col-span-full row-end-3 row-start-2 grid-cols-12 sm:gap-x-8">
      <template v-for="(category, index) in amendedCategories">
        <li v-if="category.count > 0" :key="index" class="flex col-span-full mb-4 sm:col-span-6 sm:mb-6 xl:col-span-4">
          <!-- possible refactoring: put this item in a separate molecule -->
          <article class="group flex w-full" data-component="CardOverviewAnimal">
            <AtomLink :url="getUrl(category)" class="flex w-full bg-white border-r-2 border-solid border-app-green rounded-lg overflow-hidden md:rounded-xl">
              <div class="aspect-square flex shrink-0 w-24 min-h-24 overflow-hidden">
                <div v-if="category.icon" class="w-full flex items-center justify-center">
                  <figure class="v-hover-zoom-image w-2/5 text-app-green object-cover transform transition duration-200">
                    <AtomIcon :icon="category.icon.slug" />
                    <figcaption class="app-clip">{{ category.title }}</figcaption>
                  </figure>
                </div>
              </div>
              <section class="p-y4 relative flex flex-1 flex-col justify-center pr-12 leading-tight md:pr-16 md:py-8 md:text-lg">
                <h4 class="mb-1 text-black font-semibold leading-none uppercase">{{ category.title }}</h4>
                <p class="text-black leading-none">{{ category.count }}</p>
                <AtomIconArrowRight
                  class="absolute bottom-0 right-0 mb-4 mr-4 w-3 h-3 text-app-green transform group-hover:translate-x-1 transition duration-200 sm:w-4 sm:h-4 md:mb-6 md:mr-6 md:w-5 md:h-5"
                />
              </section>
            </AtomLink>
          </article>
        </li>
      </template>
    </ul>
  </section>
</template>

<script setup lang="ts">
import { setupTranslationPrefixer } from '~/composables/util'
import { setupSiteConfig } from '~/composables/craft'

interface Props {
  translationGroup?: string
  animalCounts?: Record<string, number> | undefined
  categories: []
}

const props = defineProps<Props>()
const { site } = setupSiteConfig()

// Compute total count
const totalCount = Object.values(props.animalCounts ?? {}).reduce((a, b) => a + b, 0)

// Amend categories with count
const amendedCategories = props.categories.map((category) => ({
  ...category,
  count: props.animalCounts ? props.animalCounts[category.slug] || 0 : 0,
}))

// Function to get URL
const getUrl = (category: {
  slug: string
  url: string
  title: string
  icon?: {
    slug: string
  }
}): string => {
  switch (site) {
    case 'default':
    case 'ikZoekBaas':
    case 'mijnDierIsZoek':
      return category.url
    default:
      // We are on a subsite, therefore we will include the subsite in the url as a pre-filter
      return `${category.url}?site=${encodeURIComponent(site)}`
  }
}

const { tc_ } = setupTranslationPrefixer('AnimalCategories', props.translationGroup)
</script>
